import * as React from 'react';

import type { InputLeadingIconProps, InputValueChangeProps } from './input';

/**
 * The `displayName` of a child element's component, or `undefined` for anything
 * else (text, numbers, an element whose component has none).
 *
 * Slots are matched by name, NOT by `child.type === InputUnit`. The registry is
 * vendored into consumer repos, so a field and its slot can end up resolving to
 * different module copies — identity comparison then returns false and the slot
 * silently stops being recognised. A name survives duplication.
 *
 * Therefore **never wrap a slot in an HOC** — `withTooltip(InputClear)`,
 * `memo(InputUnit)`: the wrapper has its own name, so it stops being a slot. A
 * wrapped trigger slot renders inside the truncating value; a wrapped unit stops
 * the field auto-sizing; a wrapped `InputClear` is not removed from a refused
 * field. Put the tooltip on the slot's contents, or around the whole field. In
 * development a name that contains a slot's name but is not it warns, once per
 * slot per page.
 */
export function slotName(child: React.ReactNode): string | undefined {
  if (!React.isValidElement(child)) return undefined;
  const name = (child.type as { displayName?: string } | null)?.displayName;
  if (import.meta.env.DEV && name) warnWrappedSlot(name);
  return name;
}

/** Every component Input matches by `displayName`, in any of its slot checks. */
const SLOT_NAMES = [
  'InputLeadingIcon',
  'InputValueChange',
  'InputStateIcon',
  'InputStateInfo',
  'InputUnit',
  'InputClear',
];

const warnedWrappedSlots = new Set<string>();

/**
 * Dev-only, once per slot per page: `withTooltip(InputClear)` is named
 * "withTooltip(InputClear)", which matches no slot, so the field silently treats it
 * as plain content. Warn the moment the wrap is written rather than when its
 * symptom — an icon inside the value, a clear button on a refused field — turns up.
 */
function warnWrappedSlot(name: string) {
  if (SLOT_NAMES.includes(name)) return;
  const slot = SLOT_NAMES.find((candidate) => name.includes(candidate));
  if (!slot || warnedWrappedSlots.has(slot)) return;
  warnedWrappedSlots.add(slot);
  console.warn(
    `Input: "${name}" looks like a wrapped ${slot}, so it is not treated as one — Input ` +
      `matches slots by displayName. Don't wrap a slot in an HOC: put the tooltip (or memo) ` +
      `on its contents, or around the whole field.`
  );
}

let warnedStateInfo = false;

/**
 * Dev-only, once per page: runs on every trigger render, so an unguarded warning
 * would repeat on every re-render. No-op in production (`import.meta.env.DEV`).
 */
function warnStateInfoInTrigger() {
  if (!import.meta.env.DEV || warnedStateInfo) return;
  warnedStateInfo = true;
  console.warn(
    'splitTriggerChildren: InputStateInfo is not a trigger slot and was not rendered — it is ' +
      'a <button>, and a trigger already is one. Use InputStateIcon in the trigger and put the ' +
      'reason in an InputHelper.'
  );
}

export type TriggerChildren = {
  leadingIcon: React.ReactElement<InputLeadingIconProps> | null;
  value: React.ReactNode[];
  valueChange: React.ReactElement<InputValueChangeProps> | null;
  stateIcon: React.ReactElement | null;
};

/**
 * Splits a trigger's `children` into its slots, for the fields whose value is a
 * `<span>` rather than an `<input>` (SelectTrigger, DropdownInputTrigger): an
 * `InputLeadingIcon`, the value, an `InputValueChange`, and an `InputStateIcon`.
 * They render as SIBLINGS of the value span — the icon before it, the lockup and
 * the state glyph after it, in InputField's order — because the span truncates,
 * and anything inside it is clipped onto the value's line.
 *
 * - Slots are matched by `displayName` (see `slotName`).
 * - Fragments are looked through, nested ones too: `cond ? <>{value}{dirty &&
 *   <InputValueChange />}</> : undefined` is a natural call site, and
 *   `Children.toArray` does not flatten fragments. (`React.Fragment` is React's own
 *   symbol, so identity is safe there.) An element lifted out of a fragment gets
 *   the fragment's key as a prefix, so it cannot collide with a top-level sibling.
 * - Everything else — text, numbers, other elements — is the value, in order;
 *   `null`, `undefined` and booleans are dropped, as React would drop them.
 * - **One of each slot.** If a slot appears twice, the LAST one wins and the
 *   earlier one is not rendered.
 *
 * `InputStateInfo` is NOT a trigger slot: it is a focusable `<button>`, and a
 * trigger is already a button, so rendering it would nest interactive content
 * (axe `nested-interactive`; clicks and focus land on the wrong button). It is
 * valid on InputField, so it is an easy mistake. It is recognised, DROPPED — never
 * returned as value — and, in development only, warned about once with the fix:
 * the decorative `InputStateIcon` in the trigger, the reason in an `InputHelper`.
 */
export function splitTriggerChildren(children: React.ReactNode): TriggerChildren {
  const result: TriggerChildren = {
    leadingIcon: null,
    value: [],
    valueChange: null,
    stateIcon: null,
  };
  const visit = (nodes: React.ReactNode, keyPrefix: string) => {
    for (const child of React.Children.toArray(nodes)) {
      if (!React.isValidElement(child)) {
        result.value.push(child);
        continue;
      }
      if (child.type === React.Fragment) {
        visit((child.props as { children?: React.ReactNode }).children, `${keyPrefix}${child.key}`);
        continue;
      }
      switch (slotName(child)) {
        case 'InputLeadingIcon':
          result.leadingIcon = child as React.ReactElement<InputLeadingIconProps>;
          break;
        case 'InputValueChange':
          result.valueChange = child as React.ReactElement<InputValueChangeProps>;
          break;
        case 'InputStateIcon':
          result.stateIcon = child;
          break;
        case 'InputStateInfo':
          warnStateInfoInTrigger();
          break;
        default:
          result.value.push(
            keyPrefix ? React.cloneElement(child, { key: `${keyPrefix}${child.key}` }) : child
          );
      }
    }
  };
  visit(children, '');
  return result;
}

/**
 * Whether a trigger's value (the `value` from `splitTriggerChildren`, or any node)
 * has nothing to show — the one emptiness rule for both triggers, so the same
 * children give the same field: SelectTrigger then shows the chosen option (or its
 * placeholder), DropdownInputTrigger its placeholder.
 *
 * Empty means no value, `null` / `undefined` / booleans (dropped, as React drops
 * them), an empty string, or a list made only of those — `selected.map(...)` over
 * nothing selected gives `[]`, which is truthy but renders nothing. `0` is content.
 */
export function isEmptyTriggerValue(value: React.ReactNode): boolean {
  return React.Children.toArray(value).every((child) => child === '');
}
